import Cookies from 'js-cookie';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import { PaginationControl } from 'shared/components/pagination';
import { AsyncState } from 'shared/libs/state';
import { AppliedJobsApiService } from './applied-jobs.api.service';
import { AppliedJob, AppliedJobsIndexResponse } from './types';

export class AppliedJobsStore {
   readonly loadAppliedJobsState = new AsyncState<AppliedJobsIndexResponse>();
   readonly pagination = new PaginationControl();

   private readonly PER_PAGE = 10;

   rawAppliedJobs: AppliedJob[] = [];
   downloadingFileId: number | null = null;

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawAppliedJobs: observable,
         downloadingFileId: observable,
         appliedJobs: computed,
         totalPages: computed,
         canNext: computed,
         canPrev: computed,
         recordsCount: computed,
         coef: computed,
      });
   }

   get appliedJobs(): AppliedJob[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.rawAppliedJobs.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get totalPages(): number {
      return Math.ceil(this.rawAppliedJobs.length / this.PER_PAGE);
   }

   get canPrev(): boolean {
      return this.pagination.page > 0;
   }

   get canNext(): boolean {
      const next = this.pagination.page + 1;
      return next < this.totalPages;
   }

   get recordsCount(): string {
      if (!this.rawAppliedJobs.length) {
         return '0 - 0';
      }

      const firstRecord = this.pagination.page * this.PER_PAGE + 1;
      let lastRecord = firstRecord + this.PER_PAGE - 1;
      if (lastRecord > this.rawAppliedJobs.length) {
         lastRecord = this.rawAppliedJobs.length;
      }

      return `${firstRecord} - ${lastRecord}`;
   }

   get coef(): number {
      return this.pagination.page * this.PER_PAGE + 1;
   }

   onPrev(): void {
      if (this.canPrev) {
         this.pagination.setPage(this.pagination.page - 1);
      }
   }

   onNext(): void {
      if (this.canNext) {
         this.pagination.setPage(this.pagination.page + 1);
      }
   }

   async init(): Promise<void> {
      if (!this.ensureCandidateAccess()) {
         return;
      }

      await this.loadAppliedJobs();
   }

   async loadAppliedJobs(): Promise<void> {
      await this.loadAppliedJobsState.run(() => AppliedJobsApiService.loadAppliedJobs(), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.rawAppliedJobs = resp.response.jobApplyed ?? [];
                  this.normalizePage();
               });
               return;
            }

            if (resp.status === 400) {
               this.handleUnauthorized();
            }
         },
         onError: (error) => {
            this.handleExpiredOrUnauthorized(error.message);
         },
      });
   }

   async downloadReviewFile(fileId: number): Promise<boolean> {
      runInAction(() => {
         this.downloadingFileId = fileId;
      });

      try {
         const response = await AppliedJobsApiService.downloadAppliedFile(fileId);
         const contentDisposition =
            typeof (response.headers as { get?: (name: string) => unknown }).get === 'function'
               ? (response.headers as { get: (name: string) => unknown }).get(
                    'content-disposition'
                 )
               : (response.headers as Record<string, unknown>)['content-disposition'];
         const filename = this.extractFilename(contentDisposition);
         this.downloadBlob(response.data, filename);
         return true;
      } catch (error) {
         this.handleExpiredOrUnauthorized(this.extractErrorMessage(error));
         return false;
      } finally {
         runInAction(() => {
            this.downloadingFileId = null;
         });
      }
   }

   private extractFilename(contentDisposition: unknown): string {
      if (!contentDisposition) {
         return 'applied-file';
      }

      const dispositionValue = Array.isArray(contentDisposition)
         ? contentDisposition[0]
         : String(contentDisposition);

      const filenamePart = dispositionValue
         .split(';')
         .find((part: string) => part.toLowerCase().includes('filename='));
      if (!filenamePart) {
         return 'applied-file';
      }

      const rawName = filenamePart.split('=')[1]?.trim();
      if (!rawName) {
         return 'applied-file';
      }

      return rawName.replace(/['"]/g, '');
   }

   private extractErrorMessage(error: unknown): string {
      if (typeof error === 'object' && error !== null && 'message' in error) {
         const maybeMessage = (error as { message?: unknown }).message;
         if (typeof maybeMessage === 'string') {
            return maybeMessage;
         }
      }
      return 'Request failed';
   }

   private downloadBlob(blob: Blob, filename: string): void {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
   }

   private normalizePage(): void {
      const maxPage = Math.max(0, this.totalPages - 1);
      if (this.pagination.page > maxPage) {
         this.pagination.setPage(maxPage);
      }
   }

   private ensureCandidateAccess(): boolean {
      const userType = Cookies.get('user_type');
      const token = Cookies.get('tokenClient');

      if (!token || userType !== 'candidate') {
         this.navigate('/auth/candidate-login');
         return false;
      }

      return true;
   }

   private handleUnauthorized(): void {
      Cookies.remove('fname');
      Cookies.remove('user_id');
      Cookies.remove('user_type');
      Cookies.remove('tokenClient');
      this.navigate('/');
   }

   private handleExpiredOrUnauthorized(message: string): void {
      const normalizedMessage = message.toLowerCase();
      if (
         normalizedMessage.includes('token has expired') ||
         normalizedMessage.includes('unauthorized') ||
         normalizedMessage.includes('no response from server')
      ) {
         this.navigate('/auth/candidate-login');
      }
   }
}
